import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { confirmDialog } from "../../components/em/confirm";
import {
  DEFAULT_LAYOUT_BY_ROLE,
  WIDGET_REGISTRY,
  widgetsForRole,
  isWidgetAllowed,
} from './widgets/registry';
import {
  getLayout as apiGetLayout,
  putLayout as apiPutLayout,
  deleteLayout as apiDeleteLayout,
} from './dashboardApi';

const LS_KEY = (userId, kind) => `emhub_dash_layout_${userId || 'anon'}_${kind}`;

/**
 * Returns every widget id allowed for `kind` but NOT present in `layout`.
 * Used so newly-introduced widgets appear in the layout automatically.
 */
function appendMissingDefaults(kind, layout) {
  const defaults = DEFAULT_LAYOUT_BY_ROLE[kind] || [];
  const have = new Set(layout.map((l) => l.i));
  const appended = defaults.filter((d) => !have.has(d.i));
  return appended.length ? [...layout, ...appended] : layout;
}

/** Strip grid items whose widget id no longer exists or isn't allowed for the role. */
function filterAllowed(kind, layout) {
  return layout.filter((l) => {
    const w = WIDGET_REGISTRY[l.i];
    return isWidgetAllowed(w, kind);
  });
}

function clone(x) {
  return JSON.parse(JSON.stringify(x));
}

export default function useDashboardLayout(kind) {
  const userData = useMemo(
    () => JSON.parse(localStorage.getItem('userdata') || '{}'),
    []
  );
  const userId = userData?.id;

  const defaults = useMemo(() => clone(DEFAULT_LAYOUT_BY_ROLE[kind] || []), [
    kind,
  ]);

  const [layout, setLayoutState] = useState(defaults);
  const [hidden, setHiddenState] = useState([]);
  const [prefs, setPrefsState] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const saveTimer = useRef(null);

  // Hydrate: try backend, then localStorage, then defaults.
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      let nextLayout = defaults;
      let nextHidden = [];
      let nextPrefs = {};

      try {
        const data = await apiGetLayout(kind);
        if (Array.isArray(data?.layout) && data.layout.length) {
          nextLayout = data.layout;
        }
        if (Array.isArray(data?.hidden)) {
          nextHidden = data.hidden;
        }
        if (data?.prefs && typeof data.prefs === 'object') {
          nextPrefs = data.prefs;
        }
      } catch (err) {
        // 404 or network — fall back to localStorage
        try {
          const raw = localStorage.getItem(LS_KEY(userId, kind));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed?.layout) && parsed.layout.length)
              nextLayout = parsed.layout;
            if (Array.isArray(parsed?.hidden)) nextHidden = parsed.hidden;
            if (parsed?.prefs && typeof parsed.prefs === 'object')
              nextPrefs = parsed.prefs;
          }
        } catch (_) {}
      }

      nextLayout = appendMissingDefaults(kind, filterAllowed(kind, nextLayout));

      if (!cancelled) {
        setLayoutState(nextLayout);
        setHiddenState(nextHidden);
        setPrefsState(nextPrefs);
        setLoaded(true);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [kind, userId, defaults]);

  const persist = useCallback(
    (nextLayout, nextHidden, nextPrefs) => {
      // Local cache always.
      try {
        localStorage.setItem(
          LS_KEY(userId, kind),
          JSON.stringify({ layout: nextLayout, hidden: nextHidden, prefs: nextPrefs })
        );
      } catch (_) {}

      // Debounced backend save.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        setSaving(true);
        try {
          await apiPutLayout(kind, {
            layout: nextLayout,
            hidden: nextHidden,
            prefs: nextPrefs,
          });
          setSavedAt(new Date());
        } catch (err) {
          console.error('Failed to save dashboard layout', err);
        } finally {
          setSaving(false);
        }
      }, 350);
    },
    [kind, userId]
  );

  const setLayout = useCallback(
    (nextLayout) => {
      // react-grid-layout emits layout arrays without our custom fields.
      // Merge minW/minH from registry so constraints survive round-trips.
      const incoming = (nextLayout || []).map((item) => {
        const w = WIDGET_REGISTRY[item.i];
        return {
          ...item,
          minW: w?.minW ?? item.minW,
          minH: w?.minH ?? item.minH,
        };
      });
      // RGL only reports currently-rendered (visible) items, so a hidden
      // widget would silently drop out of the saved layout. Preserve any entry
      // RGL didn't report so hiding → re-adding restores the widget's spot.
      const incomingIds = new Set(incoming.map((i) => i.i));
      const preserved = layout.filter((l) => !incomingIds.has(l.i));
      const merged = [...incoming, ...preserved];
      setLayoutState(merged);
      if (loaded) persist(merged, hidden, prefs);
    },
    [hidden, prefs, loaded, persist, layout]
  );

  const setHidden = useCallback(
    (nextHidden) => {
      setHiddenState(nextHidden);
      if (loaded) persist(layout, nextHidden, prefs);
    },
    [layout, prefs, loaded, persist]
  );

  // Merge a patch into one widget's prefs bucket (e.g. star theme).
  const setWidgetPrefs = useCallback(
    (widgetId, patch) => {
      setPrefsState((prev) => {
        const next = {
          ...prev,
          [widgetId]: { ...(prev[widgetId] || {}), ...patch },
        };
        if (loaded) persist(layout, hidden, next);
        return next;
      });
    },
    [layout, hidden, loaded, persist]
  );

  const toggleHidden = useCallback(
    (id) => {
      const isHidden = hidden.includes(id);
      const nextHidden = isHidden
        ? hidden.filter((x) => x !== id)
        : [...hidden, id];
      let nextLayout = layout;
      // Re-adding a widget whose layout entry was lost (e.g. dropped while
      // hidden in edit mode): restore it from the role defaults, else append.
      if (isHidden && !layout.some((l) => l.i === id)) {
        const def = (DEFAULT_LAYOUT_BY_ROLE[kind] || []).find((d) => d.i === id);
        nextLayout = [...layout, def ? { ...def } : { i: id, x: 0, y: Infinity, w: 4, h: 4 }];
      }
      setHiddenState(nextHidden);
      if (nextLayout !== layout) setLayoutState(nextLayout);
      if (loaded) persist(nextLayout, nextHidden, prefs);
    },
    [hidden, layout, prefs, loaded, persist, kind]
  );

  // Apply a full snapshot (layout + hidden [+ prefs]) atomically. Used by
  // starter presets and saved presets. Persists once with the new values —
  // calling setLayout then setHidden separately would race on stale closures.
  const applyPreset = useCallback(
    ({ layout: nextLayout, hidden: nextHidden, prefs: nextPrefs }) => {
      const merged = (nextLayout || []).map((item) => {
        const w = WIDGET_REGISTRY[item.i];
        return {
          ...item,
          minW: w?.minW ?? item.minW,
          minH: w?.minH ?? item.minH,
        };
      });
      // Keep only role-allowed widgets, then re-add any missing defaults so the
      // grid never ends up short a widget the role should always have.
      const cleaned = appendMissingDefaults(kind, filterAllowed(kind, merged));
      const resolvedHidden = Array.isArray(nextHidden) ? nextHidden : hidden;
      const resolvedPrefs =
        nextPrefs && typeof nextPrefs === 'object' ? nextPrefs : prefs;

      setLayoutState(cleaned);
      setHiddenState(resolvedHidden);
      setPrefsState(resolvedPrefs);
      if (loaded) persist(cleaned, resolvedHidden, resolvedPrefs);
    },
    [kind, hidden, prefs, loaded, persist]
  );

  // Snapshot of the current personalisation state — what a saved preset stores.
  const captureSnapshot = useCallback(
    () => clone({ layout, hidden, prefs }),
    [layout, hidden, prefs]
  );

  const reset = useCallback(async () => {
    const confirmed = await confirmDialog(
      'Reset dashboard layout to the default? This cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await apiDeleteLayout(kind);
    } catch (_) {}
    try {
      localStorage.removeItem(LS_KEY(userId, kind));
    } catch (_) {}
    const fresh = clone(DEFAULT_LAYOUT_BY_ROLE[kind] || []);
    setLayoutState(fresh);
    setHiddenState([]);
    setPrefsState({});
    setSavedAt(new Date());
  }, [kind, userId]);

  // Widgets visible in this grid (respects role + hidden list).
  const visibleWidgets = useMemo(() => {
    const all = widgetsForRole(kind);
    return all.filter((w) => !hidden.includes(w.id));
  }, [kind, hidden]);

  return {
    loaded,
    layout,
    hidden,
    prefs,
    setLayout,
    setHidden,
    setWidgetPrefs,
    toggleHidden,
    applyPreset,
    captureSnapshot,
    reset,
    editMode,
    setEditMode,
    saving,
    savedAt,
    visibleWidgets,
  };
}
